import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, InputNumber, Popconfirm, Select, Skeleton, Switch, Tag, message } from 'antd';
import styled from 'styled-components';

import { request } from '~/components/Instance';

// Configurações › Extrato Bancário (CON-325): liga a aba no portal do cliente,
// o texto do aviso, de qual tarefa do Calendário sai o status e o lembrete.
const Block = styled.div`
  margin-bottom: 26px;
  max-width: 760px;
`;

const Title = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #3d2473;
  margin-bottom: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const Help = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
`;

const SOURCES = [
  { value: 'ambos', label: 'E-mail da empresa e usuários do portal' },
  { value: 'empresa', label: 'Só o e-mail da empresa' },
  { value: 'painel', label: 'Só os usuários do portal' }
];

const errorOf = (error) => {
  const data = error && error.response && error.response.data;
  if (data && data.errors) {
    const keys = Object.keys(data.errors);
    if (keys.length) return data.errors[keys[0]][0];
  }
  return 'Não foi possível salvar.';
};

export const ExtratoBancarioSettings = () => {
  const [config, setConfig] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [counts, setCounts] = useState(null);
  const [bulking, setBulking] = useState(false);

  const apply = (payload) => {
    setConfig(payload.config);
    setTasks(payload.tasks || []);
    if (payload.customers) setCounts(payload.customers);
  };

  // Liga ou desliga a aba para todos os clientes ativos de uma vez.
  const bulk = (enabled) => {
    setBulking(true);
    request('post', 'extrato-bancario/clientes', { enabled: enabled })
      .then((response) => setCounts(response.data.data.customers))
      .catch((error) => message.error(errorOf(error)))
      .finally(() => setBulking(false));
  };

  useEffect(() => {
    request('get', 'extrato-bancario/config', {}, false).then((response) => apply(response.data.data));
  }, []);

  const set = (key, value) => setConfig(Object.assign({}, config, { [key]: value }));
  const setReminder = (key, value) => setConfig(Object.assign({}, config, {
    reminder: Object.assign({}, config.reminder, { [key]: value })
  }));

  const save = () => {
    setSaving(true);
    request('put', 'extrato-bancario/config', config)
      .then((response) => apply(response.data.data))
      .catch((error) => message.error(errorOf(error)))
      .finally(() => setSaving(false));
  };

  const loadPreview = (kind) => {
    request('get', 'extrato-bancario/lembrete/preview?kind=' + kind, {}, false)
      .then((response) => setPreview(response.data.data))
      .catch((error) => message.error(errorOf(error)));
  };

  const sendTest = (kind) => {
    request('post', 'extrato-bancario/lembrete/teste', { kind: kind })
      .catch((error) => message.error(errorOf(error)));
  };

  if (!config) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }

  const reminder = config.reminder;

  return (
    <div>
      <Block>
        <Title>No portal do cliente</Title>
        <Row>
          <Switch checked={config.enabled} onChange={(value) => set('enabled', value)} id="extrato-enabled" />
          <label htmlFor="extrato-enabled">Mostrar a aba “Extrato Bancário” para os clientes</label>
        </Row>

        <Label htmlFor="extrato-notice">Texto do aviso</Label>
        <Input.TextArea
          id="extrato-notice"
          rows={3}
          maxLength={600}
          value={config.notice}
          onChange={(event) => set('notice', event.target.value)}
        />

        <div style={{ marginTop: 14 }}>
          <Label htmlFor="extrato-task">O status de cada mês vem da tarefa do Calendário</Label>
          <Select
            id="extrato-task"
            style={{ width: '100%' }}
            value={config.task_id || undefined}
            placeholder="Criar a tarefa “EXTRATOS BANCÁRIOS” no Contábil ao salvar"
            allowClear
            onChange={(value) => set('task_id', value || null)}
            showSearch
            optionFilterProp="children"
          >
            {tasks.map((task) => (
              <Select.Option key={task.id} value={task.id}>
                {task.name}{task.suggested ? ' ★' : ''}
              </Select.Option>
            ))}
          </Select>
          <Help>
            Entregue = tarefa executada ou revisada. Não entregue = “sem documento”. Sem movimento = “sem movimento”.
            A equipe pode marcar pela tela Extratos dos clientes ou pelo próprio Calendário.
          </Help>
        </div>
      </Block>

      <Block>
        <Title>Clientes com a aba</Title>
        <div style={{ fontSize: 14, marginBottom: 10 }}>
          {counts ? (
            <span>
              <strong>{counts.enabled}</strong> de {counts.active} clientes ativos estão com a aba ligada.
            </span>
          ) : null}
        </div>
        <Help style={{ marginTop: 0, marginBottom: 12 }}>
          Cada cliente liga e desliga na gaveta do cliente, aba Serviços. Aqui dá para fazer o mesmo com todos de uma vez.
        </Help>
        <Row>
          <Popconfirm
            title="Ligar a aba para todos os clientes ativos?"
            okText="Ligar para todos"
            cancelText="Cancelar"
            onConfirm={() => bulk(true)}
          >
            <Button icon="check-circle" loading={bulking}>Ligar para todos os clientes ativos</Button>
          </Popconfirm>
          <Popconfirm
            title="Desligar a aba de todos os clientes ativos? Os envios já feitos continuam guardados."
            okText="Desligar de todos"
            cancelText="Cancelar"
            onConfirm={() => bulk(false)}
          >
            <Button icon="stop" loading={bulking}>Desligar de todos</Button>
          </Popconfirm>
        </Row>
        <Row>
          <Switch
            id="extrato-auto"
            checked={config.auto_new_customers}
            onChange={(value) => set('auto_new_customers', value)}
          />
          <label htmlFor="extrato-auto">Ligar automaticamente em cliente novo</label>
        </Row>
      </Block>

      <Block>
        <Title>Lembrete automático por e-mail</Title>
        <Row>
          <Switch checked={reminder.enabled} onChange={(value) => setReminder('enabled', value)} id="extrato-reminder" />
          <label htmlFor="extrato-reminder">Mandar lembrete todo mês</label>
        </Row>

        <Row>
          <span>Lembrete no dia</span>
          <InputNumber min={1} max={28} value={reminder.day} onChange={(value) => setReminder('day', value)} disabled={!reminder.enabled} />
          <span style={{ color: '#8c8c8c' }}>para quem ainda não mandou o mês anterior</span>
        </Row>

        <Row>
          <Switch
            size="small"
            checked={reminder.chase_enabled}
            onChange={(value) => setReminder('chase_enabled', value)}
            disabled={!reminder.enabled}
          />
          <span>Cobrança no dia</span>
          <InputNumber
            min={1}
            max={28}
            value={reminder.chase_day}
            onChange={(value) => setReminder('chase_day', value)}
            disabled={!reminder.enabled || !reminder.chase_enabled}
          />
          <span style={{ color: '#8c8c8c' }}>só para quem continua sem mandar (é também o prazo mostrado ao cliente)</span>
        </Row>

        <Label htmlFor="extrato-source">Enviar para</Label>
        <Select id="extrato-source" style={{ width: 360 }} value={reminder.source} onChange={(value) => setReminder('source', value)}>
          {SOURCES.map((source) => (
            <Select.Option key={source.value} value={source.value}>{source.label}</Select.Option>
          ))}
        </Select>
        <Help>
          Clientes ativos. Fica de fora quem já mandou ou foi marcado “sem movimento”, os e-mails que voltaram (blacklist)
          e quem clicou em “parar de receber”. Usuários do portal com notificações desligadas não recebem.
        </Help>

        <Row style={{ marginTop: 16 }}>
          <Button icon="team" onClick={() => loadPreview('lembrete')}>Quem recebe o lembrete</Button>
          <Button icon="team" onClick={() => loadPreview('cobranca')}>Quem recebe a cobrança</Button>
          <Button icon="mail" onClick={() => sendTest('lembrete')}>Enviar teste para mim</Button>
        </Row>

        {preview && (
          <Alert
            type="info"
            showIcon
            message={
              (preview.kind === 'cobranca' ? 'Cobrança' : 'Lembrete') + ' de ' + preview.competence.split('-').reverse().join('/') +
              ', se saísse agora: ' + preview.customers + (preview.customers === 1 ? ' cliente, ' : ' clientes, ') +
              preview.emails + (preview.emails === 1 ? ' e-mail' : ' e-mails')
            }
            description={
              preview.list.length ? (
                <div style={{ maxHeight: 180, overflow: 'auto' }}>
                  {preview.list.map((item) => (
                    <div key={item.id}>
                      {item.name} <Tag style={{ marginLeft: 6 }}>{item.emails.length}</Tag>
                    </div>
                  ))}
                </div>
              ) : null
            }
          />
        )}
      </Block>

      <Button type="primary" icon="save" loading={saving} onClick={save}>Salvar</Button>
    </div>
  );
};

export default ExtratoBancarioSettings;
